import { useState, useCallback, useRef, useEffect } from 'react';
import { HOUR_HEIGHT_PX } from '../types';

/**
 * Drag an event to a new time, and in week view to a different day.
 *
 * The grid could resize an event but not move one, so rescheduling meant
 * opening the dialog and typing dates — the one calendar gesture every user
 * arrives already knowing. This is the counterpart to `useEventResize` and
 * deliberately mirrors its shape: pointer deltas held in a ref, listeners
 * registered only while a gesture is live, and the commit handed to the caller
 * so the page owns the API call and the recurring-scope prompt.
 *
 * The target day is resolved by HIT-TESTING the column under the pointer at
 * drop rather than by dividing the horizontal delta by a column width. Column
 * widths vary with the viewport and the time gutter, and a drag that ends
 * outside any column then lands the event on a day the user never pointed at.
 * Hit-testing degrades correctly instead: no column under the pointer means
 * the day is unchanged.
 */

/** Below this the gesture is a click, not a drag — events must stay clickable. */
const DRAG_THRESHOLD_PX = 4;

interface DragState {
  instanceId: string;
  /** Day column the gesture STARTED in — see onDragStart. */
  originDayKey: string | null;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  moved: boolean;
}

export interface UseEventDragResult {
  draggingEventId: string | null;
  /** Live pixel offset for the preview transform; null when not dragging. */
  dragOffset: { x: number; y: number } | null;
  /** `instanceId` is the OCCURRENCE id (EventInstance.id), not the series eventId. */
  onDragStart: (instanceId: string, e: React.MouseEvent) => void;
}

/** Where a drag started and where it was dropped, in calendar days. */
export interface DragDrop {
  /** Day the grabbed SEGMENT sat in, or null if it could not be resolved. */
  originDayKey: string | null;
  /** Day dropped on, or null if the drop missed every column. */
  targetDayKey: string | null;
  minuteDelta: number;
}

/**
 * The day under the pointer, read from the `data-calendar-date` marker the
 * week grid's columns and the month grid's cells both carry. One attribute for
 * both, so a second drop-target convention cannot drift from the first.
 */
function dayKeyAtPointer(x: number, y: number): string | null {
  const el = document.elementFromPoint(x, y);
  const cell = el?.closest('[data-calendar-date]');
  return cell?.getAttribute('data-calendar-date') ?? null;
}

export function useEventDrag(
  onMove: (instanceId: string, drop: DragDrop) => void,
  hourHeightPx: number = HOUR_HEIGHT_PX,
  snapMinutes: number = 15,
  /**
   * `dayOnly` is month view: its vertical axis is weeks, not hours, so pixels
   * of vertical travel must NOT become a time shift — dragging down one row
   * would otherwise silently move the event about 100 minutes later as well as
   * to another day. The time of day is carried across untouched.
   */
  options: { dayOnly?: boolean } = {},
): UseEventDragResult {
  const { dayOnly = false } = options;
  const pxPerMinute = hourHeightPx / 60;
  const [state, setState] = useState<DragState | null>(null);
  const stateRef = useRef<DragState | null>(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  const onDragStart = useCallback((instanceId: string, e: React.MouseEvent) => {
    // Left button only: a right-click must still reach the context menu.
    if (e.button !== 0) return;
    // NOT preventDefault: mousedown's default action is what focuses the
    // button, and suppressing it left a clicked event unfocused, breaking the
    // tab order from wherever the user had just clicked. Text selection during
    // a drag is handled by the preview's `pointer-events: none` and the
    // gesture's own threshold instead.
    e.stopPropagation();
    const initial: DragState = {
      instanceId,
      // WHICH SEGMENT was grabbed. A multi-day or overnight event is drawn as a
      // separate block in every day it overlaps, and every block carries the
      // same event id — so the id alone cannot say which one the user took
      // hold of. Without this, dropping the Tuesday half of a Mon->Tue night
      // shift onto Tuesday moved the event's START to Tuesday, silently
      // shifting it a whole day; a five-day event grabbed on day three and
      // nudged one day jumped three.
      originDayKey: dayKeyAtPointer(e.clientX, e.clientY),
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      dy: 0,
      moved: false,
    };
    stateRef.current = initial;
    setState(initial);
  }, []);

  useEffect(() => {
    if (!state) return;

    const handleMouseMove = (e: MouseEvent) => {
      const current = stateRef.current;
      if (!current) return;
      const dx = e.clientX - current.startX;
      const dy = e.clientY - current.startY;
      const moved =
        current.moved || Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX;
      stateRef.current = { ...current, dx, dy, moved };
      setState({ ...stateRef.current });
    };

    const handleMouseUp = (e: MouseEvent) => {
      const current = stateRef.current;
      stateRef.current = null;
      setState(null);
      if (!current) return;

      if (!current.moved) return; // A click; the button's onClick opens the event.

      // Swallow the click this mouseup is about to synthesise, or finishing a
      // drag would also open the event detail dialog on top of the change.
      const swallow = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      // If no click follows (drag ended off the element), don't leave it armed.
      window.setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 0);

      const snapPx = snapMinutes * pxPerMinute;
      const minuteDelta = dayOnly ? 0 : Math.round(current.dy / snapPx) * snapMinutes;
      const targetDayKey = dayKeyAtPointer(e.clientX, e.clientY);
      const movedDay = targetDayKey !== null && targetDayKey !== current.originDayKey;
      if (minuteDelta === 0 && !movedDay) return;
      onMoveRef.current(current.instanceId, {
        originDayKey: current.originDayKey,
        targetDayKey,
        minuteDelta,
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // Register once per gesture, as useEventResize does — stateRef carries the
    // per-pixel updates without re-subscribing.
  }, [state !== null, pxPerMinute, snapMinutes, dayOnly]);

  return {
    draggingEventId: state?.moved ? state.instanceId : null,
    dragOffset: state?.moved ? { x: state.dx, y: state.dy } : null,
    onDragStart,
  };
}
